/**
 * "On this device" summary (#2021) — counts of DOMAIN data cached locally in
 * IndexedDB, distinct from the sync OUTBOX (visit_sync_queue / file queue).
 *
 * The outbox answers "what still needs delivering?"; this answers "what working
 * data is cached on this device right now?" (visits, survey answers, the product
 * catalog). Synced data is NOT removed from these stores — only the outbox row
 * is — so this reflects the device's actual cached state, which is what reps
 * want visibility into when switching devices / debugging.
 */

import { STORES, count, getAll } from './indexedDB';

export interface LocalDataItem {
  key: string;
  label: string;
  count: number;
}

async function safeCount(store: string): Promise<number> {
  try {
    return await count(store); // native O(1) store.count() — no record materialization
  } catch {
    return 0;
  }
}

interface OrderLineLike {
  orderqty?: number;
  quantity_on_hand?: number | null;
  is_oos?: boolean;
}

/**
 * "On this device" counts. Covers every user-meaningful DOMAIN object cached
 * locally (#2021 + #2022): visits, survey answers, photos/documents (the file
 * queue split by type), captured order / inventory / OOS lines (stored under
 * `orders:*` singleton keys, split by kind so they match the per-type sync
 * chip rather than all lumping under "Orders"), and the product catalog. NOT
 * the sync outbox — that's the "Synced today" breakdown. Zero-count rows are
 * omitted. Display order matches the push order below.
 */
export async function getLocalDataSummary(): Promise<LocalDataItem[]> {
  const items: LocalDataItem[] = [];

  items.push({ key: 'visits', label: 'Visits', count: await safeCount(STORES.VISIT_TASK_SESSIONS) });
  items.push({ key: 'surveys', label: 'Survey answers', count: await safeCount(STORES.VISIT_SURVEY_RESPONSES) });

  // Photos + documents share the file queue; split by record type.
  let photos = 0;
  let documents = 0;
  try {
    const files = (await getAll(STORES.VISIT_FILE_QUEUE)) as Array<{ type?: string }>;
    for (const f of files) {
      if (f.type === 'document') documents += 1;
      else photos += 1;
    }
  } catch {
    /* leave at 0 */
  }
  items.push({ key: 'photos', label: 'Photos', count: photos });
  items.push({ key: 'documents', label: 'Documents', count: documents });

  // Captured lines under `orders:<visitLocalId>` singletons — classify each line
  // by kind so this agrees with the split sync chip (an inventory/OOS capture is
  // NOT an order). A single line can be both an order and an inventory count.
  let orderLines = 0;
  let inventoryLines = 0;
  let oosLines = 0;
  try {
    const singles = (await getAll(STORES.VISIT_SINGLETONS)) as Array<{
      key?: string;
      orders?: Array<{ products?: OrderLineLike[] }>;
    }>;
    for (const s of singles) {
      if (typeof s.key !== 'string' || !s.key.startsWith('orders:') || !Array.isArray(s.orders)) continue;
      for (const submission of s.orders) {
        for (const line of submission.products ?? []) {
          if (line.is_oos) oosLines += 1;
          else if (line.quantity_on_hand != null) inventoryLines += 1;
          if ((line.orderqty ?? 0) > 0) orderLines += 1;
        }
      }
    }
  } catch {
    /* leave at 0 */
  }
  items.push({ key: 'orders', label: 'Orders', count: orderLines });
  items.push({ key: 'inventory', label: 'Inventory', count: inventoryLines });
  items.push({ key: 'oos', label: 'Out of stock', count: oosLines });

  items.push({ key: 'products', label: 'Products cached', count: await safeCount(STORES.PRODUCTS) });

  // Omit zero-count rows so the popup shows only what's actually on the device.
  return items.filter((i) => i.count > 0);
}
